import FilterItem from "@app/filter/FilterItem";
import { Label } from "@comps/ui/label";
import { SearchableSelect, SearchableSelectOption } from "@comps/ui/searchable-select";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@comps/ui/select";
import { Separator } from "@comps/ui/separator";
import { Switch } from "@comps/ui/switch";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@comps/ui/tooltip";
import { CompozzError, isCompozzError } from "@dto/CompozzError";
import Field from "@dto/fields";
import { ObjectInfos } from "@dto/objects";
import { AND, Filter, GROUPABLE_TYPES } from "@dto/records";
import { ASC, DESC, Group, GroupDisplayType, LEFT, NONE, SortType, TOP, View } from "@dto/views";
import { GRAY_300_COLOR, GRAY_600_COLOR } from "@utils/colors";
import { BASE_URL } from "@utils/constants";
import { clearData } from "@utils/data";
import { getPathKeys, getSrcFieldKey, isSortable, pathKeyToPath } from "@utils/model";
import { toastError } from "@utils/toasts";
import { getWsIconStyle, TW_FilterEditorNewFilter, TW_FilterEditorRemoveFilter, TW_FilterEditorRemoveFilterDisabled, TW_GroupEditor, TW_GroupEditorDisplayTypeSelect, TW_GroupEditorItem, TW_GroupEditorItemAdvanced, TW_GroupEditorItemAdvancedIcon, TW_GroupEditorItemAdvancedIconDisabled, TW_GroupEditorItemAdvancedItem, TW_GroupEditorItemAdvancedLabel, TW_GroupEditorItemAdvancedLabelGrow, TW_GroupEditorItemAdvancedOptions, TW_GroupEditorItemAdvancedSeparator, TW_GroupEditorPathSelect, TW_GroupEditorSortSelect, TW_GroupFilterItems, useHover } from "@utils/twClasses";
import { put } from "@utils/utils";
import { MessageQuestion } from "iconsax-react";
import { Ellipsis, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { translate, translateHtml } from "src/i18n/i18n";
import { useModelStore } from "src/store";


interface GroupEditorProps {
    groupFields: Group[]
    onClose: () => void
    disabled: boolean
}

function GroupEditor({ groupFields, onClose, disabled }: GroupEditorProps) {

    const updateSelectedView = useModelStore((state) => state.updateSelectedView);
    const workspace = useModelStore((state) => state.selectedWorkspace);
    const selectedObjectInfos = useModelStore((state) => state.selectedObjectInfos);
    const sharedObjectInfos = useModelStore((state) => state.sharedObjectInfos);
    const selectedView = useModelStore((state) => state.selectedView);
    const { t } = useTranslation();

    const fields = selectedObjectInfos?.fields;
    if (!fields) {
        return null;
    }

    let groupByPathKeys: Map<string, Group> = new Map();
    const groupPathKeys: string[] = [];
    groupFields.forEach((groupField) => {
        groupByPathKeys.set(groupField.pathKey, groupField);
        groupPathKeys.push(groupField.pathKey);
    })

    const onViewChange = (objectInfos: ObjectInfos, view: View) => {
        updateSelectedView(objectInfos, view, null, sharedObjectInfos);
        // No need to call fetchView as it is triggered by the view change
        // fetchView(view);
        // The lines on screen do not fit the new groups: they are dropped before
        // being fetched again
        clearData();
    }


    function validateGroup(group: Group | null) {
        if (!group) {
            return;
        }

        const newGroupFields = (groupFields || []).map(g => {
            if (g.pathKey === group.pathKey) {
                return group;
            }
            return g;
        });

        const newGroup = newGroupFields.find(g => g.pathKey === group.pathKey);
        if (!newGroup) {
            newGroupFields.push(group);
        }

        validateAllGroupFields(newGroupFields);
    }

    const onRemoveGroup = (groupPathKey: string) => {
        if (!groupPathKey || disabled) {
            return;
        }

        const newGroupFields = (groupFields || []).filter(g => g.pathKey !== groupPathKey);
        validateAllGroupFields(newGroupFields);
    }

    function validateAllGroupFields(groupFields: Group[] | null) {
        if (!workspace?._key) {
            console.error("Workspace key is not set");
            return;
        }

        const newView: View = {
            ...selectedView!,
            groups: groupFields
        }
        put<View>(`${BASE_URL}/model/workspaces/${workspace._key}/objects/${selectedObjectInfos?.object?._key}/views`, newView).then(response => {
            if (response) {
                if (isCompozzError(response)) {
                    const cError = response as CompozzError;
                    if (cError.i18n) {
                        toastError(translate(t, cError.i18n, cError.params));
                    } else {
                        toastError(cError.message);
                    }
                    onClose();
                }
            } else {
                onViewChange(selectedObjectInfos!, newView);
            }
        })
    }


    const itemKeyPref = selectedView?._key + "-group-"
    if (disabled) {
        return (
            <div className={TW_GroupEditor}>
                <TooltipProvider>
                    <Tooltip delayDuration={100}>
                        <TooltipTrigger>
                            <div className={TW_GroupEditor}>
                                {groupFields.length > 0 &&
                                    groupFields.map((groupField, index) => {
                                        return <GroupItem key={itemKeyPref + index} group={groupField} index={index} usedGroupPathKeys={groupPathKeys} onClose={onClose} disabled={disabled} validateGroup={validateGroup} onRemoveGroup={onRemoveGroup} />
                                    })}
                                <GroupItem key={itemKeyPref + groupFields.length} group={null} index={groupFields.length} usedGroupPathKeys={groupPathKeys} onClose={onClose} disabled={disabled} validateGroup={validateGroup} onRemoveGroup={onRemoveGroup} />
                            </div>
                        </TooltipTrigger>
                        <TooltipContent>
                            {t("group.edition.forbidden.tooltip")}
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>
            </div>
        )
    } else {
        return (
            <div className={TW_GroupEditor}>
                {groupFields.length > 0 &&
                    groupFields.map((groupField, index) => {
                        return <GroupItem key={itemKeyPref + index} group={groupField} index={index} usedGroupPathKeys={groupPathKeys} onClose={onClose} disabled={disabled} validateGroup={validateGroup} onRemoveGroup={onRemoveGroup} />
                    })}
                <GroupItem key={itemKeyPref + groupFields.length} group={null} index={groupFields.length} usedGroupPathKeys={groupPathKeys} onClose={onClose} disabled={disabled} validateGroup={validateGroup} onRemoveGroup={onRemoveGroup} />
            </div>
        )
    }
}

interface GroupItemProps {
    group: Group | null,
    index: number,
    validateGroup: (group: Group) => void,
    onRemoveGroup: (groupPathKey: string) => void,
    usedGroupPathKeys: string[],
    onClose: () => void,
    disabled: boolean
}

function GroupItem({ group, index, usedGroupPathKeys, onClose, disabled, validateGroup, onRemoveGroup }: GroupItemProps) {

    const { t } = useTranslation();
    const selectedView = useModelStore((state) => state.selectedView);
    const objectByKeys = useModelStore((state) => state.objectByKeys);
    const [selectedPathKey, setSelectedPathKey] = useState<string | undefined>(undefined);
    const [selectedSortType, setSelectedSortType] = useState<SortType>(NONE);
    const [hideEmptyGroups, setHideEmptyGroups] = useState<boolean>(group?.hideEmptyGroups ?? false);
    const [applyGroupFilter, setApplyGroupFilter] = useState<boolean>(group?.applyGroupFilter ?? false);
    const [showSummaries, setShowSummaries] = useState<boolean>(!(group?.hideSummaries ?? false));
    const [groupFilter, setGroupFilter] = useState<Filter | null>(group?.filter ?? null);
    const [displayType, setDisplayType] = useState<GroupDisplayType>((index != usedGroupPathKeys.length - 1) ? TOP : (group?.displayType ?? TOP));
    const [advancedMode, setAdvancedMode] = useState<boolean>(false);
    const selectedObjectInfos = useModelStore((state) => state.selectedObjectInfos);
    const relationByKeys = useModelStore((state) => state.relationByKeys);
    const fieldByKeys = useModelStore((state) => state.fieldByKeys);
    const wsColor = useModelStore((state) => state.wsColor);
    const hoverTrash = useHover(getWsIconStyle(GRAY_600_COLOR), getWsIconStyle(wsColor));
    const hoverAdvanced = useHover(getWsIconStyle(GRAY_600_COLOR), getWsIconStyle(wsColor));

    useEffect(() => {
        if (group) {
            const fields = selectedObjectInfos?.fields;
            if (!fields) {
                return;
            }
            const pathKeys = getPathKeys(selectedObjectInfos, fields, relationByKeys, fieldByKeys, true);
            const selectedPathKeyIndex = pathKeys.findIndex(pathKey => pathKey === group.pathKey);
            if (selectedPathKeyIndex > -1) {
                setSelectedPathKey(group.pathKey);
            } else {
                const fieldKey = getSrcFieldKey(group.pathKey);
                setSelectedPathKey(fieldKey?.fieldKey);
            }
            
            setSelectedSortType(group.sort);
            setHideEmptyGroups(group.hideEmptyGroups ?? false);
            setApplyGroupFilter(group.applyGroupFilter ?? false);
            setShowSummaries(!(group.hideSummaries ?? false));
            setGroupFilter(group?.filter ?? null);
            setDisplayType((index != usedGroupPathKeys.length - 1) ? TOP : (group?.displayType ?? TOP));
        }
    }, [group, index, usedGroupPathKeys, selectedObjectInfos?.fields, relationByKeys, fieldByKeys]);

    const onPathChange = (pathKey: string) => {
        setSelectedPathKey(pathKey);

        const newGroup: Group = {
            pathKey: pathKey,
            sort: selectedSortType,
            hideEmptyGroups: hideEmptyGroups,
            displayType: displayType,
            applyGroupFilter: applyGroupFilter,
            filter: groupFilter,
            hideSummaries: !showSummaries
        }
        validateGroup(newGroup);
    }

    const onSortTypeChange = (sortType: SortType) => {
        setSelectedSortType(sortType);

        if (!selectedPathKey) {
            return;
        }

        const newGroup: Group = {
            pathKey: selectedPathKey,
            sort: sortType,
            hideEmptyGroups: hideEmptyGroups,
            displayType: displayType,
            applyGroupFilter: applyGroupFilter,
            filter: groupFilter,
            hideSummaries: !showSummaries
        }
        validateGroup(newGroup);
    }

    const onHideEmptyGroupsChange = (hideEmptyGroups: boolean) => {
        setHideEmptyGroups(hideEmptyGroups);

        if (!selectedPathKey) {
            return;
        }

        const newGroup: Group = {
            pathKey: selectedPathKey,
            sort: selectedSortType,
            hideEmptyGroups: hideEmptyGroups,
            displayType: displayType,
            applyGroupFilter: applyGroupFilter,
            filter: groupFilter,
            hideSummaries: !showSummaries
        }
        validateGroup(newGroup);
    }

    const onDisplayTypeChange = (displayType: GroupDisplayType) => {
        setDisplayType(displayType);

        if (!selectedPathKey) {
            return;
        }

        const newGroup: Group = {
            pathKey: selectedPathKey,
            sort: selectedSortType,
            hideEmptyGroups: hideEmptyGroups,
            displayType: displayType,
            applyGroupFilter: applyGroupFilter,
            filter: groupFilter,
            hideSummaries: !showSummaries
        }
        validateGroup(newGroup);
    }

    const onAdvancedModeChange = () => {
        if (!selectedPathKey) {
            return;
        }
        setAdvancedMode(!advancedMode);
    }

    const onFiltersChange = (filters: Filter[] | null) => {
        if (!filters) {
            return;
        }
        const newGroupFilter: Filter = {
            singleFilter: null,
            groupFilter: {
                filters: filters,
                combinator: AND
            }
        }

        setGroupFilter(newGroupFilter);

        if (!selectedPathKey) {
            return;
        }

        const newGroup: Group = {
            pathKey: selectedPathKey,
            sort: selectedSortType,
            hideEmptyGroups: hideEmptyGroups,
            displayType: displayType,
            applyGroupFilter: applyGroupFilter,
            filter: newGroupFilter,
            hideSummaries: !showSummaries
        }
        validateGroup(newGroup);
    }

    const onApplyGroupFilterChange = (applyGroupFilter: boolean) => {
        setApplyGroupFilter(applyGroupFilter);

        if (!selectedPathKey || groupFilter == null) {
            return;
        }

        const newGroup: Group = {
            pathKey: selectedPathKey,
            sort: selectedSortType,
            hideEmptyGroups: hideEmptyGroups,
            displayType: displayType,
            applyGroupFilter: applyGroupFilter,
            filter: groupFilter,
            hideSummaries: !showSummaries
        }
        validateGroup(newGroup);
    }

    const onShowSummariesChange = (showSummaries: boolean) => {
        setShowSummaries(showSummaries);

        if (!selectedPathKey) {
            return;
        }

        const newGroup: Group = {
            pathKey: selectedPathKey,
            sort: selectedSortType,
            hideEmptyGroups: hideEmptyGroups,
            displayType: displayType,
            applyGroupFilter: applyGroupFilter,
            filter: groupFilter,
            hideSummaries: !showSummaries
        }
        validateGroup(newGroup);
    }

    const onRemoveCurrentGroup = () => {
        if (!selectedPathKey || disabled) {
            return;
        }
        onRemoveGroup(selectedPathKey);
    }

    const fields = selectedObjectInfos?.fields;
    if (!fields) {
        return null;
    }
    const pathKeys = getPathKeys(selectedObjectInfos, fields, relationByKeys, fieldByKeys, false);
    const fieldByPathKeys = new Map<string, Field>();
    pathKeys.forEach((pathKey, index) => {
        if (!pathKey) {
            return;
        }
        const field = fields[index];
        if (!field) {
            return;
        }
        fieldByPathKeys.set(pathKey!, field);
    });
    const selectedPathKeyIndex = pathKeys.findIndex(pathKey => pathKey === selectedPathKey);
    let sortable = false;
    if (selectedPathKeyIndex > -1) {
        const selectedField = fieldByPathKeys.get(selectedPathKey!);
        sortable = (selectedField && isSortable(selectedField.type)) ?? false;
    }

    const disabledTrash = disabled || !selectedPathKey;
    const disabledAdvanced = !selectedPathKey;
    let groupObjectInfos: ObjectInfos | null = null;
    let divKey = selectedView?._key + "-group-" + index + "-" + selectedPathKey;
    if (selectedPathKey) {
        divKey += "-" + selectedSortType;
        const groupPath = pathKeyToPath(selectedPathKey, relationByKeys);
        if (groupPath) {
            groupObjectInfos = objectByKeys?.get(groupPath.object) || null;
        }
    } else {
        divKey += "-new";
    }

    const groupFilterItems = groupFilter?.groupFilter?.filters ?? null;

    const isDisabled = disabled || !selectedPathKey;
    let separatorHeight = 32;
    if (applyGroupFilter) {
        separatorHeight += ((groupFilterItems?.length ?? 0) + 1 ) * 11;
    }
    const separatorHeightRem = separatorHeight * 0.25;
    const separatorHeightStyle = {
        height: separatorHeightRem + "rem"
    }

    return (
        <div className={TW_GroupEditorItem} key={divKey}>
            <div className={TW_FilterEditorNewFilter}>
                <SearchableSelect
                    className={TW_GroupEditorPathSelect}
                    placeholder={t("group.path.placeholder")}
                    value={selectedPathKey}
                    onValueChange={onPathChange}
                    disabled={disabled || selectedPathKey != undefined}
                    options={pathKeys.flatMap((pathKey): SearchableSelectOption[] => {
                        const field = fieldByPathKeys.get(pathKey!);
                        if (!field) {
                            return [];
                        }
                        if (!GROUPABLE_TYPES.includes(field.type)) {
                            return [];
                        }
                        if (pathKey === null) {
                            return [{ value: field._key, label: field.name + " (" + t("group.field.invalid") + ")", disabled: true }];
                        }
                        var disabled;
                        if (selectedView?.pivot?.pivotPathKey != null && selectedView?.pivot?.pivotPathKey === pathKey) {
                            disabled = true;
                        } else {
                            disabled = usedGroupPathKeys.includes(pathKey)
                        }
                        return [{ value: pathKey, label: field.name, disabled: disabled }];
                    })}
                />
                <Select onValueChange={onSortTypeChange} value={selectedSortType} disabled={isDisabled || !sortable}>
                    <SelectTrigger className={TW_GroupEditorSortSelect}>
                        <SelectValue placeholder={t("group.type.placeholder")} />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem key={divKey + "group-type-none"} value={NONE}>{t("group.type.name." + NONE)}</SelectItem>
                        <SelectItem key={divKey + "group-type-asc"} value={ASC}>{t("group.type.name." + ASC)}</SelectItem>
                        <SelectItem key={divKey + "group-type-desc"} value={DESC}>{t("group.type.name." + DESC)}</SelectItem>
                    </SelectContent>
                </Select>

                <Ellipsis className={disabledAdvanced ? TW_GroupEditorItemAdvancedIconDisabled : TW_GroupEditorItemAdvancedIcon} onClick={onAdvancedModeChange} {...hoverAdvanced} />
                {!disabledTrash && <Trash2 className={TW_FilterEditorRemoveFilter} onClick={onRemoveCurrentGroup} {...hoverTrash} />}
                {disabledTrash && <Trash2 className={TW_FilterEditorRemoveFilterDisabled} />}
            </div>
            {advancedMode && <div className={TW_GroupEditorItemAdvanced}>
                <Separator className={TW_GroupEditorItemAdvancedSeparator} orientation="vertical" style={separatorHeightStyle} />
                <div className={TW_GroupEditorItemAdvancedOptions}>
                    <div className={TW_GroupEditorItemAdvancedItem}>
                        <Label className={TW_GroupEditorItemAdvancedLabelGrow}>{t("group.advanced.hide.empty")}</Label>
                        <Switch checked={hideEmptyGroups} disabled={isDisabled} onCheckedChange={onHideEmptyGroupsChange} style={{ backgroundColor: (hideEmptyGroups ? wsColor : GRAY_300_COLOR) }} />
                    </div>
                    <div className={TW_GroupEditorItemAdvancedItem}>
                        <Label className={TW_GroupEditorItemAdvancedLabelGrow}>{t("group.advanced.show.summaries")}</Label>
                        <Switch checked={showSummaries} disabled={isDisabled} onCheckedChange={onShowSummariesChange} style={{ backgroundColor: (showSummaries ? wsColor : GRAY_300_COLOR) }} />
                    </div>
                    <div className={TW_GroupEditorItemAdvancedItem}>
                        <div className="flex flex-row justify-start items-center gap-2  w-full grow">
                            <Label className={TW_GroupEditorItemAdvancedLabel}>{t("group.advanced.display.type")}</Label>
                            <TooltipProvider>
                                <Tooltip delayDuration={100}>
                                    <TooltipTrigger className="inline-block">
                                        <MessageQuestion className="ml-2 h-6 w-6 min-h-6 min-w-6 text-gray-600 inline-block" />
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        {translateHtml(t, "group.advanced.display.type.tooltip", [])}
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                        </div>
                        <Select onValueChange={onDisplayTypeChange} value={displayType} disabled={isDisabled || index != usedGroupPathKeys.length - 1}>
                            <SelectTrigger className={TW_GroupEditorDisplayTypeSelect}>
                                <SelectValue placeholder={t("group.advanced.display.type.placeholder")} />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem key={divKey + "group-display-type-top"} value={TOP}>{t("group.advanced.display.type.name." + TOP)}</SelectItem>
                                <SelectItem key={divKey + "group-display-type-left"} value={LEFT}>{t("group.advanced.display.type.name." + LEFT)}</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div className={TW_GroupEditorItemAdvancedItem}>
                        <Label className={TW_GroupEditorItemAdvancedLabelGrow}>{t("group.advanced.apply.filter")}</Label>
                        <Switch checked={applyGroupFilter} disabled={isDisabled} onCheckedChange={onApplyGroupFilterChange} style={{ backgroundColor: (applyGroupFilter ? wsColor : GRAY_300_COLOR) }} />
                    </div>
                    {selectedPathKey && groupObjectInfos && applyGroupFilter && <GroupFilterItems groupFilterItems={groupFilterItems} objectInfos={groupObjectInfos} onFiltersChange={onFiltersChange} usedFilterPathKeys={usedGroupPathKeys} disabled={isDisabled} />}
                </div>
            </div>
            }
        </div>
    )
}

interface GroupFilterItemsProps {
    groupFilterItems: Filter[] | null,
    objectInfos: ObjectInfos,
    onFiltersChange: (filters: Filter[] | null) => void,
    usedFilterPathKeys: string[],
    disabled: boolean
}

function GroupFilterItems({ groupFilterItems, objectInfos, onFiltersChange, usedFilterPathKeys, disabled }: GroupFilterItemsProps) {

    const selectedView = useModelStore((state) => state.selectedView);

    const onFilterChange = (filter: Filter | null) => {
        if (!filter || !filter.groupFilter?.filters?.length || !filter.groupFilter?.filters[0]?.singleFilter?.pathKey) {
            return;
        }

        const pathKey = filter.groupFilter?.filters[0]?.singleFilter?.pathKey;
        const newFilters = (groupFilterItems || []).map(f => {
            if (f.groupFilter?.filters?.length && f.groupFilter?.filters[0]?.singleFilter?.pathKey === pathKey) {
                return filter;
            }
            return f;
        });
        const newFilter = newFilters.find(f => f.groupFilter?.filters?.length && f.groupFilter?.filters[0]?.singleFilter?.pathKey === pathKey);
        if (!newFilter) {
            newFilters.push(filter);
        }
        onFiltersChange(newFilters);
    }

    const onFilterRemove = (pathKey: string | null) => {
        if (!pathKey) {
            return;
        }

        const newFilters = (groupFilterItems || []).filter(f => {
            return f.groupFilter?.filters?.length && f.groupFilter?.filters[0]?.singleFilter?.pathKey !== pathKey
        });
        onFiltersChange(newFilters);
    }

    const divKey = selectedView?._key + "-group-filter";
    return (
        <div className={TW_GroupFilterItems}>
            {groupFilterItems?.map((groupFilterItem, index) => {
                return <FilterItem key={divKey + "group-filter-" + index} filter={groupFilterItem} objectInfos={objectInfos} onFilterChange={onFilterChange} onFilterRemove={onFilterRemove} usedFilterPathKeys={usedFilterPathKeys} disabled={disabled} />
            })}
            <FilterItem key={divKey + "group-filter-" + groupFilterItems?.length} filter={null} objectInfos={objectInfos} onFilterChange={onFilterChange} onFilterRemove={onFilterRemove} usedFilterPathKeys={usedFilterPathKeys} disabled={disabled} />
        </div>
    )
}

export default GroupEditor;
